// frontend/src/pages/DeveloperInfo.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Code as CodeIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../contexts/ThemeContext';
import { documentationApi } from '../services/apiClient';
import DocumentViewer from '../components/DocumentViewer';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Swagger UI loaded from CDN at runtime (not bundled — too large for Vite build)
const SWAGGER_UI_VERSION = '5.18.2';
const SWAGGER_UI_CDN = `https://unpkg.com/swagger-ui-dist@${SWAGGER_UI_VERSION}`;

/**
 * Get auth token from secure storage with fallback
 */
const getTokenFromStorage = async (key) => {
  try {
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }
    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    return localStorage.getItem(key);
  }
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

/**
 * Load a script from a URL, returning a promise.
 * Skips if a script with the same src is already in the document.
 */
const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

/**
 * Load a CSS stylesheet from a URL.
 * Skips if a link with the same href is already in the document.
 */
const loadStylesheet = (href) => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
};

/**
 * Swagger UI loaded from CDN at runtime (not bundled).
 * swagger-ui-react is ~2.5MB and causes OOM during Vite production builds.
 * Instead, we load swagger-ui-dist from unpkg CDN and initialize it into a div.
 */
const SwaggerExplorer = () => {
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const containerRef = useRef(null);
  const tokenRef = useRef(null);
  const swaggerInstanceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // Load auth token
        const token = await getTokenFromStorage('auth_token');
        tokenRef.current = token;

        // Load Swagger UI CSS + JS from CDN
        loadStylesheet(`${SWAGGER_UI_CDN}/swagger-ui.css`);
        await loadScript(`${SWAGGER_UI_CDN}/swagger-ui-bundle.js`);
        await loadScript(`${SWAGGER_UI_CDN}/swagger-ui-standalone-preset.js`);

        if (cancelled || !containerRef.current) return;

        // Initialize Swagger UI into the container div
        swaggerInstanceRef.current = window.SwaggerUIBundle({
          url: `${API_BASE_URL}/api/docs/openapi.json`,
          domNode: containerRef.current,
          presets: [
            window.SwaggerUIBundle.presets.apis,
            window.SwaggerUIStandalonePreset,
          ],
          plugins: [
            window.SwaggerUIBundle.plugins.DownloadUrl,
          ],
          layout: 'StandaloneLayout',
          docExpansion: 'list',
          defaultModelsExpandDepth: -1,
          filter: true,
          tryItOutEnabled: false,
          supportedSubmitMethods: ['get', 'post', 'put', 'patch', 'delete'],
          requestInterceptor: (req) => {
            if (tokenRef.current) {
              req.headers['Authorization'] = `Bearer ${tokenRef.current}`;
            }
            return req;
          },
        });

        if (!cancelled) setReady(true);
      } catch (err) {
        console.error('Failed to load Swagger UI:', err);
        if (!cancelled) setLoadError(err.message);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  // Refresh token periodically so "Try it out" stays authenticated
  useEffect(() => {
    const interval = setInterval(async () => {
      const token = await getTokenFromStorage('auth_token');
      tokenRef.current = token;
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loadError) {
    return (
      <Alert severity="error">
        Failed to load API Explorer: {loadError}
      </Alert>
    );
  }

  return (
    <Box>
      {!ready && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading API Explorer...
          </Typography>
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          '& .swagger-ui .topbar': { display: 'none' },
          '& .swagger-ui': { fontFamily: 'inherit' },
        }}
      />
    </Box>
  );
};

const DeveloperInfo = () => {
  const { t } = useThemeSettings();
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentationList, setDocumentationList] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    fetchDocumentationList();
  }, []);

  const fetchDocumentationList = async () => {
    try {
      setLoading(true);
      const response = await documentationApi.getList();
      setDocumentationList(response.data.docs);
      setError(null);
    } catch (err) {
      console.error('Error fetching documentation list:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDoc = (doc) => {
    setSelectedDoc(doc);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDoc(null);
  };

  // Group documentation by category
  const groupedDocs = documentationList.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {});

  // Category metadata
  const categoryMetadata = {
    setup: {
      title: 'Setup & Configuration',
      icon: '⚙️',
      description: 'Initial setup, configuration, and deployment guides',
      color: '#2196f3'
    },
    branding: {
      title: 'Branding & Customization',
      icon: '🎨',
      description: 'Customizing the look and feel of the platform',
      color: '#ff5722'
    },
    internationalization: {
      title: 'Internationalization (i18n)',
      icon: '🌍',
      description: 'Multi-language support and translation guides',
      color: '#4caf50'
    },
    security: {
      title: 'Authentication & Security',
      icon: '🔐',
      description: 'Security, authentication flows, and user management',
      color: '#f44336'
    },
    workflows: {
      title: 'Workflows & Automation',
      icon: '🤖',
      description: 'Automated processes and workflow management',
      color: '#9c27b0'
    },
    api: {
      title: 'API Documentation',
      icon: '🛠️',
      description: 'API reference and usage guides',
      color: '#3f51b5'
    },
    user: {
      title: 'User Documentation',
      icon: '📚',
      description: 'End-user guides and manuals',
      color: '#ff9800'
    }
  };

  return (
    <Box
    className="page-transition-enter"
    sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" className="slide-down" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon fontSize="large" color="primary" />
          {t ? t('DeveloperInfo.developer_documentation') : 'Developer Documentation'}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t ? t('DeveloperInfo.admin_only') : 'Admin Only'}</strong> - This section is only visible to administrators.
          </Typography>
        </Alert>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<DescriptionIcon />} iconPosition="start" label="Documentation" />
        <Tab icon={<ApiIcon />} iconPosition="start" label="API Explorer" />
      </Tabs>

      {/* Tab 0: Documentation (existing content) */}
      <TabPanel value={tabValue} index={0}>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <div className="spinner" />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" className="slide-up" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Documentation Sections */}
        {!loading && !error && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {t ? t('DeveloperInfo.documentation_categories') : 'Documentation Categories'}
            </Typography>

            {Object.entries(groupedDocs).map(([categoryId, docs]) => {
              const section = categoryMetadata[categoryId] || {
                title: categoryId,
                icon: '📄',
                description: 'Documentation',
                color: '#757575'
              };
              return (
            <Accordion
              key={categoryId}
              className="card-animated card-enter"
              expanded={expanded === categoryId}
              onChange={handleChange(categoryId)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${categoryId}-content`}
                id={`${categoryId}-header`}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography fontSize="1.5rem">{section.icon}</Typography>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {section.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {section.description}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${docs.length} ${docs.length === 1 ? 'doc' : 'docs'}`}
                    size="small"
                    sx={{ bgcolor: section.color, color: 'white' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {docs.map((doc) => (
                    <Grid item xs={12} md={6} key={doc.id}>
                      <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <DescriptionIcon color="action" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight="bold">
                              {doc.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {doc.description}
                          </Typography>
                          <Chip
                            label={doc.file}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </CardContent>
                        <CardActions>
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDoc(doc)}
                          >
                            {t ? t('DeveloperInfo.view_doc') : 'View Documentation'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
              );
            })}
          </Box>
        )}
      </TabPanel>

      {/* Tab 1: API Explorer (Swagger UI) */}
      <TabPanel value={tabValue} index={1}>
        <SwaggerExplorer />
      </TabPanel>

      {/* Document Viewer Dialog */}
      <DocumentViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        docId={selectedDoc?.id}
        docTitle={selectedDoc?.title}
      />
    </Box>
  );
};

export default DeveloperInfo;
